import moment from 'moment-timezone';
import momentDurationFormat from 'moment-duration-format';
import _ from 'lodash';
import Routes from 'routes';
import UAParser from 'ua-parser-js';

const AMAZON_SLUG = 'amazon-delivery-service-partner';
const DSP_SLUG = 'delivery-service-partner';
const AMAZON_AGWS_SLUG = 'test_account';

momentDurationFormat(moment);

export const humanize = _.memoize(name => name.split('_').map(word => _.upperFirst(word)).join(' '));

export function formatDuration(value) {
  const duration = moment.isDuration(value) ? value : moment.duration(value, 'seconds');
  const absDuration = moment.duration(Math.abs(moment.isDuration(value) ? value.asSeconds() : value), 'seconds');

  if (value && Math.round(absDuration.asMonths()) > 0) {
    return duration.format('M[m] d[d]');
  } else if (value && Math.round(absDuration.asDays()) > 0) {
    return duration.format('d[d] h[h]');
  } else if (value && absDuration.asHours() > 0) {
    return duration.format('h[h] m[min]');
  } return 'N/A';
}

const IS_NUM = /^\d+$/;
const IS_BOOL = /^(true|false)$/;
export function getParamsFromQueryString() {
  const params = new URLSearchParams(window.location.search);

  return Array.from(params.keys()).reduce((acc, filter) => {
    const value = params.get(filter);

    switch (true) {
      case typeof value === 'string' && IS_NUM.test(value):
        acc[filter] = parseInt(params.get(filter), 10);
        break;
      case typeof value === 'string' && IS_BOOL.test(value):
        acc[filter] = value === 'true';
        break;
      default:
        acc[filter] = value;
    }

    return acc;
  }, {});
}

export function objectToFormUrlEncoded(obj) {
  function recur(newObj, propName, currVal) {
    if (Array.isArray(currVal) || Object.prototype.toString.call(currVal) === '[object Object]') {
      Object.keys(currVal).forEach(v => recur(newObj, `${propName}[${v}]`, currVal[v]));
      return newObj;
    }

    return Object.assign(newObj, { [propName]: currVal });
  }

  const keys = Object.keys(obj);
  return keys.reduce((newObj, propName) => recur(newObj, propName, obj[propName]), {});
}

export function objectToFormData(obj, form, namespace) {
  const data = form || new FormData();
  let formKey;

  Object.keys(obj).forEach((property) => {
    if (obj[property] !== null && obj[property] !== undefined) {
      if (namespace) {
        formKey = `${namespace}[${property}]`;
      } else {
        formKey = property;
      }

      if (typeof obj[property] === 'object' && !(obj[property] instanceof File)) {
        objectToFormData(obj[property], data, property);
      } else {
        data.append(formKey, obj[property]);
      }
    }
  });

  return data;
}

export function getTextareaCaretPos(textarea) {
  let caretOffset = 0;

  if (document.selection) {
    textarea.focus();
    const selection = document.selection.createRange();
    selection.moveStart('character', -textarea.value.length);
    caretOffset = selection.text.length;
  } else if (textarea.selectionStart || textarea.selectionStart === '0') {
    caretOffset = textarea.selectionStart;
  }

  return caretOffset;
}

const sortUserByName = (left, right) => {
  const leftName = left.label.toUpperCase();
  const rightName = right.label.toUpperCase();
  if (leftName < rightName) {
    return -1;
  }
  if (leftName > rightName) {
    return 1;
  }
  return 0;
};

export function groupedUsersForSelect(currentUserId, users = [], applySort = false) {
  const groupedOptions = [];
  const regular = [];
  const admins = [];
  let owner = null;

  users.forEach((user) => {
    if (user.id !== currentUserId) {
      if (user.owner) {
        owner = { value: user.id, label: user.name };
      } else {
        (user.admin ? admins : regular).push({ value: user.id, label: user.name });
      }
    }
  });

  if (regular.length) {
    if (applySort) {
      regular.sort(sortUserByName);
    }
    groupedOptions.push(['Regular', regular]);
  }

  if (admins.length) {
    if (applySort) {
      admins.sort(sortUserByName);
    }
    groupedOptions.push(['Admins', admins]);
  }

  if (owner) {
    groupedOptions.push(['Owner', [owner]]);
  }

  return groupedOptions;
}

export function truncateFileName(filename = '') {
  const extension = filename.split('.').pop().toLowerCase();
  const name = filename.split('.').shift();

  return `${_.truncate(name, { length: 30 })}.${extension}`;
}

function calcKeyJsonReplacer(name, value) {
  if (_.isFunction(value)) {
    return value.name;
  }

  if (_.isObject(value) && value.$$typeof === Symbol.for('react.element')) {
    return value.type.name;
  }

  return JSON.stringify(value);
}

export function calcKey(value, index) {
  const valuesToStringify = [];
  const stringValue = (_.isString(value) || _.isNumber(value))
    ? value
    : JSON.stringify(value, calcKeyJsonReplacer);

  valuesToStringify.push(stringValue);

  if (index) {
    valuesToStringify.push(index);
  }

  return valuesToStringify.join('-');
}

export function findInGroupedList(groupedList, predicate) {
  for (let i = 0, ii = groupedList.length; i < ii; i += 1) {
    const foundValue = groupedList[i][1].find(predicate);

    if (foundValue) {
      return foundValue;
    }
  }

  return null;
}

export function isFilePreviewable(document) {
  return (
    document.previewable || /\.pdf$/i.test(document.filename)
  );
}

export function prepareBackendValidation(error, fieldsMapping) {
  const { errors } = (error.responseJSON || { errors: {} });

  return Object.keys(errors).reduce((acc, key) => {
    if (Array.isArray(errors[key])) {
      acc[key] = errors[key]
        .map((message) => {
          if (fieldsMapping && fieldsMapping[key]) {
            return `${fieldsMapping[key]} ${message}`;
          }

          return message;
        })
        .join(', ');
    } else if (typeof errors[key] === 'string') {
      acc[key] = errors[key];
    }

    return acc;
  }, {});
}

export function generateClickableTooltip(jqueryElemToAddTooltip, title, description) {
  // eslint-disable-next-line max-len
  const clickableTooltip = '<div class="form-field-more-info js-form-field-more-info"><div class="form-field-more-info__circle">i</div><div class="form-field-more-info__circle-hover">See instructions</div></div>';

  jqueryElemToAddTooltip.append(clickableTooltip);

  jqueryElemToAddTooltip.find('.js-form-field-more-info').on('click', (e) => {
    e.preventDefault();
    const $axModal = $('.ax-modal');
    $axModal.find('.header-title').text(title);
    $axModal.find('.header-description').html(description);
    $axModal.toggleClass('ax-display-modal');
  });
}

export function getAppName() {
  const currentAppNode = document.querySelector('meta[name="app"]');

  if (currentAppNode) {
    return currentAppNode.content;
  }

  return null;
}

export function getUrlParametres() {
  const paramsString = document.location.href.split('?')[1];
  if (paramsString) {
    return paramsString.split('&').reduce((acc, item) => {
      const parts = item.split('=');
      return {
        ...acc,
        [parts[0]]: parts[1],
      };
    }, {});
  }
  return {};
}

export function getSignInUrl() {
  const appName = getAppName();
  switch (appName) {
    case 'application':
      return Routes.user_session_path();
    case 'portal':
      return Routes.new_identity_session_path();
    default:
      return '/';
  }
}

/* global $ */
export function validateWrapper(key, errorMessage, validateFunc) {
  const errorLabel = $(`.js-error-${key}`);
  const field = $(`.js-${key}`);
  if (validateFunc()) {
    errorLabel.hide();
    field.addClass('has-success');
    field.removeClass('has-error');
    return true;
  }
  errorLabel.html(errorMessage);
  errorLabel.show();
  field.removeClass('has-success');
  field.addClass('has-error');
  return false;
}

export function validateRequiredField(formName, key, errorMessage) {
  return validateWrapper(key, errorMessage, () => $(`#${formName}_${key}`).val());
}

export function validateRequiredRadioField(formName, key, errorMessage) {
  return validateWrapper(key, errorMessage, () => $(`input[name='${formName}[${key}]']:checked`).val());
}

export function validateZipcode(formName, key, errorMessage) {
  return validateWrapper(
    key,
    errorMessage,
    () => /(^\d{5}$)|(^\d{5}-\d{4}$)|(^\d{6}$)/.test($(`#${formName}_${key}`).val())
  );
}

export function validateSsn(formName, key, errorMessage) {
  return validateWrapper(key, errorMessage, () => /^\d{3}-\d{2}-\d{4}$/.test($(`#${formName}_${key}`).val()));
}

export function validateRequiredDropdown(formName, key, errorMessage) {
  return validateWrapper(key, errorMessage, () => $(`select[name*=${formName}\\[${key}\\]]`).val());
}

export function validateInteger(formName, key, errorMessage) {
  return validateWrapper(key, errorMessage, () => /^[0-9]+$/.test($(`#${formName}_${key}`).val()));
}

export function validateExempt(formName, key, errorMessage) {
  const value = $(`#${formName}_${key}`).val();
  return validateWrapper(key, errorMessage, () => value === 'EXEMPT' || value === '');
}

export function prefillAxDropdown(formName, inputName, value) {
  $(`select[name="${formName}[${inputName}]"]`).find(`option:contains("${value}")`).attr('selected', 'selected');
  $(`.js-${inputName} .ax-select-selected`).text(value).val(value);
  $(`.ax-select-items div:contains("${value}")`).addClass('ax-same-as-selected');
}

export function setupW4Common(gon) {
  // Setup datepicker
  window.datepickerLocalize('.js-datepicker', {
    useCurrent: false,
    locale: gon.funnel_locale,
  });

  // Mark required fields
  $('.required').addClass('required_field');

  // input masks
  $(':input').inputmask({
    rightAlign: 0,
  });

  //  hide extraneous labels for checkboxes
  $('.control-label.ax-textarea-label').hide();

  // clickable tooltips
  const ref = $('.clickable-tooltip');
  let toolTipElement;
  let title;
  let body;
  for (let i = 0, len = ref.length; i < len; i += 1) {
    toolTipElement = ref[i];
    title = toolTipElement.dataset.tooltipTitle;
    body = toolTipElement.dataset.tooltipBody;
    generateClickableTooltip($(toolTipElement), title, body);
  }
}

export function setupW4FormEmployer(formName, gon) {
  setupW4Common(gon);

  // Used to remove auto-generated help-blocks
  $('.help-block').remove();

  $('div.checkbox').has('input[disabled]').css('pointer-events', 'none');

  const validator = $(`form.${formName}`).validate({
    rules: {
      [`${formName}[employer_name]`]: {
        required: {
          depends() {
            return !$(`#${formName}_employer_name`).val();
          },
        },
      },
      [`${formName}[employer_address]`]: {
        required: {
          depends() {
            return !$(`#${formName}_employer_address`).val();
          },
        },
      },
      [`${formName}[employer_city]`]: {
        required: {
          depends() {
            return !$(`#${formName}_employer_city`).val();
          },
        },
      },
      [`${formName}[employer_state]`]: {
        required: {
          depends() {
            return !$(`#${formName}_employer_state`).val();
          },
        },
      },
      [`${formName}[employer_zip]`]: {
        required: {
          depends() {
            return !$(`#${formName}_employer_zip`).val();
          },
        },
      },
      [`${formName}[first_date_of_employment]`]: {
        required: {
          depends() {
            return !$(`#${formName}_first_date_of_employment`).val();
          },
        },
      },
      [`${formName}[employer_id_number]`]: {
        required: {
          depends() {
            return !$(`#${formName}_employer_id_number`).val();
          },
        },
      },
    },
    messages: {
      [`${formName}[employer_name]`]: {
        required: "Please provide the employer's name",
      },
      [`${formName}[employer_address]`]: {
        required: "Please provide the employer's address",
      },
      [`${formName}[employer_city]`]: {
        required: "Please provide the employer's city",
      },
      [`${formName}[employer_state]`]: {
        required: "Please provide the employer's state",
      },
      [`${formName}[employer_zip]`]: {
        required: "Please provide the employer's zip",
      },
      [`${formName}[first_date_of_employment]`]: {
        required: 'Please fill in the required field',
      },
      [`${formName}[employer_id_number]`]: {
        required: 'Please provide the Employer ID Number (EIN)',
      },
    },
    ignore: ':hidden:not(select, #i9_signature, #i9_preparer_signature)',
    errorElement: 'small',
    errorPlacement(error, element) {
      error.addClass('help-block');
      error.insertAfter(element.parents('.form-group').children().last());
    },
    highlight(element) {
      $(element).parents('.form-group').addClass('has-error').removeClass('has-success');
    },
    unhighlight(element) {
      $(element).parents('.form-group').addClass('has-success').removeClass('has-error');
    },
  });

  return validator;
}

export function setupW4FormForApplicant(formName, mountSignature, gon) {
  setupW4Common(gon);

  // Setup Signature component
  if (mountSignature) {
    window.mountReactComponent(
      '#signatures',
      window.onboardiq.PortalW4FederalFormSigning,
      'portalW4FederalFormSigning', () => ({
        type: 'PORTAL_W4_FEDERAL_FORM_SIGNING/SETUP_STORE',
        payload: {
          uploadConfigs: gon.upload_configs,
          formName,
        },
      })
    );
    // change placeholder text for state dropdown
    $('.js-state .ax-select-selected').text('Select State');

    $(`.${formName} .obiq-signature__header-text`).text('');
    $('#employee-signature').parent().addClass(`form-group ax js-signature ax-${formName}-question-field`);

    $('.obiq-signature__header-button').on('click', (e) => {
      $(e.target).closest('.has-error').removeClass('has-error');
      $('.js-error-signature').text('');
    });
  }

  const validator = $(`.${formName}`).validate({
    errorElement: 'small',
    errorPlacement(error, element) {
      error.addClass('help-block');
      $(element).parents('.form-group').append(error);
    },
    highlight(element) {
      $(element).parents('.form-group').addClass('has-error').removeClass('has-success');
    },
    unhighlight(element) {
      $(element).parents('.form-group').addClass('has-success').removeClass('has-error');
    },
    ignore: ':hidden:not(select)',
  });

  return validator;
}

export function setupPreviewSubmitButtons(validator, signatureSelector, gon) {
  let newValidator = validator;
  const bootstrapValidate = () => {
    let bsvalid = true;
    bsvalid = $('form').valid() && bsvalid;
    return bsvalid;
  };

  const mql = window.matchMedia('(max-width: 600px)');
  let isMobile = mql.matches;
  const updateScreen = (e) => {
    if (e.matches) { isMobile = true; } else {
      isMobile = false;
      $('.return-message').hide();
    }
  };
  mql.addListener(updateScreen);
  const preparerValidation = (valid) => {
    if (signatureSelector === '#i9_signature, #i9_preparer_signature') {
      if ($("input[type=radio][name='i9[preparer_certification]']:checked").val() === 'A preparer(s) and/or translator(s) assisted the employee in completing Section 1') {
        $('#i9_preparer_last_name, #i9_preparer_first_name, #i9_preparer_address, #i9_preparer_city, #i9_preparer_state, #i9_preparer_zip').prop('required', valid);
      }
    }
  };

  /* eslint-disable consistent-return */
  const w4sSignatureDateValidate = () => {
    const w4sSignature = document.getElementById('w4s_signature');

    if (!w4sSignature) {
      return false;
    }

    const signatureValue = w4sSignature.value;
    if (signatureValue !== '') {
      $('#prefill_date').val('true');
    } else {
      $('#prefill_date').val('false');
    }
  };

  const showSignature = (innerValidator) => {
    const newInnerValidator = innerValidator;
    $('.information-step').hide();
    $('.submit-step').hide();
    $('.signature-step').show();
    if (!isMobile) { $('.return-message.signature-step').hide(); }

    preparerValidation(true);
    w4sSignatureDateValidate();
    if ($('.signed-and-previewed').length === 0) {
      setTimeout(() => {
        $('button.us-doc-signature-upload-canvas-off').click();
        $('button.obiq-signature__clear_button').click();
      });
    }
    newInnerValidator.settings.ignore = `:hidden:not(select, ${signatureSelector})`;
    return newInnerValidator;
  };

  const hideSignature = (innerValidator, uploadCanvas) => {
    const newInnerValidator = innerValidator;
    $('.signature-step').hide();
    $('.submit-step').hide();
    preparerValidation(false);
    w4sSignatureDateValidate();
    $('.information-step').show();
    if (!isMobile) { $('.return-message.information-step').hide(); }
    if (uploadCanvas) {
      setTimeout(() => {
        $('.us-doc-signature-upload-canvas').click();
      });
    }
    newInnerValidator.settings.ignore = ':hidden:not(select)';
    return newInnerValidator;
  };

  newValidator = hideSignature(newValidator, false);
  const firstPreview = `<input type='hidden' id='first-preview' name='[${gon.form_name}]first_preview' value='true' />`;
  $(`.${gon.form_name}-information-step`).append(firstPreview);

  const showPreview = (event, markPreviewed) => {
    $(event.target).removeAttr('target');
    $(event.target).formAction = gon.create_url;
    if (markPreviewed) {
      $('#submit-sign-button').addClass('signed-and-previewed');
    }
  };

  const generatePreview = () => {
    $('form')[0].action = gon.preview_url;
    $('form').attr('target', '_blank');
    $('form').submit();
  };

  $('#download-info-button').click((e) => {
    e.preventDefault();
    if (bootstrapValidate()) {
      generatePreview();
    } else {
      document.querySelector('.has-error').scrollIntoView({ behavior: 'smooth' });
    }
  });

  $('#submit-info-button').click((e) => {
    e.preventDefault();
    if (bootstrapValidate()) {
      newValidator = showSignature(newValidator);
    } else {
      document.querySelector('.has-error').scrollIntoView({ behavior: 'smooth' });
    }
  });

  $('#download-sign-button').click((e) => {
    e.preventDefault();
    const uaparser = new UAParser();
    const { name, version } = uaparser.getOS();
    if (name === 'iOS') {
      const majorVersion = version.split('.')[0];
      if (Number(majorVersion) <= 11) {
        const { title, description } = $('#older-device-warning')[0].dataset;
        const $axModal = $('.ax-modal');
        $axModal.find('.header-title').text(title);
        $axModal.find('.header-description').html(description);
        $axModal.toggleClass('ax-display-modal');
      }
    }
    if (bootstrapValidate()) {
      generatePreview();
      setTimeout(() => {
        showPreview(e, true);
      });
    } else {
      document.querySelector('.has-error').scrollIntoView({ behavior: 'smooth' });
    }
  });

  $('#submit-sign-button').click((e) => {
    e.preventDefault();
    if (bootstrapValidate()) {
      $('#first-preview').remove();
      $('.signature-step').hide();
      $('#opt-out').hide();
      $('.submit-step').show();
    } else {
      document.querySelector('.has-error').scrollIntoView({ behavior: 'smooth' });
    }
  });

  $('#back-to-form-button').click(() => {
    const hasSignedAndPreviewed = $('.signed-and-previewed').length > 0;
    const signatureConfirmed = $('.obiq-signature__canvas').not(':hidden').hasClass('obiq-signature__canvas_uploaded');

    if (hasSignedAndPreviewed && !signatureConfirmed) {
      $('#preview-button').removeClass('signed-and-previewed');
      $('button.us-doc-signature-upload-canvas-off').click();
    } else if (!hasSignedAndPreviewed && signatureConfirmed) {
      $('button.us-doc-signature-upload-canvas-off').click();
      $('button.obiq-signature__clear_button').click();
    }

    newValidator = hideSignature(newValidator, true);
  });

  $('#preview-submit-button').click(() => {
    generatePreview();
    setTimeout(() => {
      $('form')[0].action = gon.create_url;
      $('form').removeAttr('target');
    });
  });

  $('#submit-button').click((e) => {
    e.preventDefault();
    if (bootstrapValidate()) {
      $('form')[0].action = gon.create_url;
      $('form').removeAttr('target');
      $('form').submit();
    } else {
      document.querySelector('.has-error').scrollIntoView({ behavior: 'smooth' });
    }
  });

  return newValidator;
}

export function hasOneBrandAndBrandBasedPermissions(brands) {
  // If no brands are passed as an argument, default to gon.brands
  const currentBrands = brands || gon.brands;
  return currentBrands && currentBrands.length === 1 && gon.enforce_brand_based_permissions;
}

export function isAmazon(accountSlug) {
  return [DSP_SLUG, AMAZON_SLUG].includes(accountSlug);
}

export function isAmazonAGWS(accountSlug) {
  return accountSlug === AMAZON_AGWS_SLUG;
}

export function isRegularUser(gon) {
  if (Boolean(gon.permissions.admin) || gon.super_user || gon.user_type === 'owner') {
    return false;
  }

  return true;
}

export function mergePaginatedData(currentPage, currentData, newData) {
  if (currentPage > 1) return [...currentData, ...newData];

  return newData;
}
